<template>
  <v-app>
    <div class="tw-grid tw-gap-[20px] tw-p-[30px_40px] tw-relative tw-text-secondary">
      <div>
        <h3 class="tw-text-primary tw-text-[24px] tw-font-semibold">
          Submit for Payout - Order #{{ delivery.id }}
        </h3>
        <v-btn
          text fab small
          class="tw-absolute tw-top-[5px] tw-right-[5px]"
          @click="closeModal"
        >
          <v-icon size="15">fa-times</v-icon>
        </v-btn>
      </div>

      <div>
        <h4 class="tw-font-semibold tw-text-[18px] tw-mb-[15px]">
          Important:
        </h4>
        <p class="tw-mb-[15px]">To receive your payout, you must upload a <strong>signed invoice or a clear image of the customer-signed document.</strong> This is required to begin the review process.</p>
        <p class="tw-mb-[15px]">If any changes occurred during the delivery — such as:</p>
        <ul class="tw-list-disc tw-list-inside tw-mb-[15px]">
          <li>Added items</li>
          <li>Increased working hours</li>
          <li>Extra tips from the customer</li>
          <li>Any other adjustments affecting the total amount</li>
        </ul>
        <p class="tw-mb-[15px]">Please describe them clearly in the <strong>Notes/Comments</strong> section below.</p>
        <p class="tw-mb-[15px]">Our admin team will review the signed document, verify details with the customer, and reach out to arrange your payout accordingly.</p>
      </div>

      <div>
        <h4 class="tw-font-semibold tw-text-[18px] tw-mb-[15px]">
          Upload Image of Signed Document
        </h4>

        <div class="tw-flex tw-items-center tw-gap-[20px] tw-mb-[20px]">
          <div class="tw-relative">
            <input
              type="file"
              multiple
              accept=".jpg, .jpeg, .png, .pdf"
              class="tw-absolute tw-inset-0 tw-z-[1] tw-opacity-0 tw-cursor-pointer"
              @change="handleFileChange"
            />
            <v-btn
              elevation="0"
              color="light"
            >
              Upload Files (JPG, JPEG, PNG, PDF)
            </v-btn>
          </div>

          <p v-if="$v.$error" class="!tw-mb-0 tw-text-[red] tw-text-[14px]">At least 1 to 5 files are required</p>
        </div>

        <ul class="tw-grid sm:tw-grid-cols-2 tw-gap-[10px] !tw-p-0 tw-mb-[20px]">
          <li
            v-for="(file, index) in files"
            :key="index"
            class="tw-flex tw-items-center tw-justify-between tw-gap-[20px] tw-text-[14px] tw-text-gray-700 tw-bg-gray-100 tw-p-[8px_12px] tw-rounded-[6px]"
          >
            <span class="tw-truncate tw-w-full">{{ file.name }}</span>
            <div class="tw-flex tw-items-center tw-gap-[8px]">
              <v-btn icon small :href="getFileUrl(file)" target="_blank" title="View file">
                <v-icon size="16">fa-eye</v-icon>
              </v-btn>
              <v-btn icon small @click="removeFile(index)" title="Remove file">
                <v-icon size="16">fa-times</v-icon>
              </v-btn>
            </div>
          </li>
        </ul>

        <textarea
          v-model="helper_note"
          class="tw-w-full tw-min-h-[100px] tw-p-[10px_15px] tw-border tw-border-solid tw-border-[#ddd] tw-rounded-[8px]"
          placeholder="Please enter any notes or comments regarding your documents or payment release here."
        />
      </div>

      <div class="tw-flex tw-items-center tw-justify-end tw-gap-[10px]">
        <v-btn
          elevation="0"
          color="light"
          @click="closeModal"
        >
          Cancel
        </v-btn>
        <v-btn
          elevation="0"
          color="primary"
          @click="complete"
        >
          Submit for Review
        </v-btn>
      </div>
    </div>
  </v-app>
</template>

<script>
import { mapActions } from 'vuex';
import { maxLength, minLength, required } from 'vuelidate/lib/validators';

export default {
  props: {
    delivery: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      files: [],
      helper_note: ''
    };
  },

  methods: {
    ...mapActions('dashboard', ['completeDelivery']),

    handleFileChange(event) {
      const newFiles = Array.from(event.target.files);

      // Filter out duplicates by name and size
      const existingFiles = this.files.map(f => f.name + f.size);
      const uniqueNewFiles = newFiles.filter(f => !existingFiles.includes(f.name + f.size));

      this.files = [...this.files, ...uniqueNewFiles];

      // Clear input so re-selecting the same file works
      event.target.value = null;
    },

    getFileUrl(file) {
      return URL.createObjectURL(file);
    },

    removeFile(index) {
      this.files.splice(index, 1);
    },

    async complete() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return false;
      }

      const formData = new FormData();
      this.files.forEach(file => {
        formData.append('files[]', file);
      });

      const note = this.helper_note
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>');

      formData.append('helper_note', note);

      this.closeModal();

      await this.completeDelivery({
        delivery_id: this.delivery.id,
        formData: formData
      });
    },

    closeModal() {
      this.$emit('close');
    }
  },

  validations: {
    files: {
      required: required,
      minLength: minLength(1),
      maxLength: maxLength(5)
    }
  },
}
</script>
